import { useAppSelector } from 'app/redux';
import { RecentlyUpdatedBlockDetails } from 'common/api/dto/get-block.dto';
import {
  useGetPaginatedPartialBlockDetailsQuery,
  useGetUnpaginatedPartialBlockDetailsQuery,
} from 'common/api/endpoints/block';
import { handleError } from 'common/api/utils/handleError';
import { AsyncCsvButton } from 'common/components/CsvButton/AsyncCsvButton';
import {
  DataTable,
  DataTableMobile,
  DataTableProps,
  MobileStyling,
  TableSortFilterParams,
} from 'common/components/DataTable';
import { usePSFQuery } from 'common/hooks';
import useWindowSize from 'common/hooks/useWindowSize';
import { PaginatedResult } from 'common/models';
import { mobile } from 'common/styles/breakpoints';
import {
  seasonSliceName,
  SeasonState,
} from 'features/navbar/components/SeasonSelector/seasonSlice';
import { useRbac } from 'features/rbac';
import { FC, useEffect, useMemo } from 'react';
import { Constants } from 'utils/constants';
import { PSFKeys, usePSFState } from 'utils/hooks/usePSFState';
import {
  blockIdFilter,
  blockStatusFilter,
  lotFilter,
  notesFilter,
} from 'utils/tableFilters/blockFilters';
import {
  GrowerBlockTableItem,
  useGrowerBlockTableData,
} from '../hooks/useGrowerBlockTableData';
import { useGrowerBlockTableDataMobile } from '../hooks/useGrowerBlockTableDataMobile';
import { formatBlockChangesData } from '../utils/exportBlockChanges';
import { growersTableFilters } from './GrowersTable';

const filterParams: TableSortFilterParams[] = [
  blockIdFilter,
  blockStatusFilter,
  ...growersTableFilters,
  lotFilter,
  notesFilter,
];

export const BlockChangesTable: FC = () => {
  const { selectedSeason } = useAppSelector<SeasonState>(
    state => state[seasonSliceName],
  );
  const { userHasPermission } = useRbac();
  const { query, setQuery } = usePSFState(PSFKeys.BlockChanges);
  const {
    data,
    isLoading,
    isFetching,
    pagination,
    applyFilters,
    filters,
    error: getBlocksError,
  } = usePSFQuery<PaginatedResult<RecentlyUpdatedBlockDetails>>(
    useGetPaginatedPartialBlockDetailsQuery,
    {},
    { ...query },
    setQuery,
    selectedSeason?.id ? { seasonId: selectedSeason.id } : {},
  );
  const blocks = useMemo(() => data?.results ?? [], [data]);
  const { columns, data: tableData } = useGrowerBlockTableData(blocks);
  const columnsMobile = useGrowerBlockTableDataMobile();

  const { width } = useWindowSize();
  const isMobile = width < parseInt(mobile, 10);
  const showExportButton =
    !isLoading && !isFetching && userHasPermission('export:block-changes');

  const ExportBlockChangesBtn = () => (
    <AsyncCsvButton<RecentlyUpdatedBlockDetails[]>
      label={`Export ${selectedSeason?.year}`}
      fetcher={useGetUnpaginatedPartialBlockDetailsQuery}
      params={{
        seasonId: selectedSeason?.id as number,
        filters: query?.filters,
      }}
      formatter={formatBlockChangesData}
      filenameOptions={{
        name: `block_changes_${selectedSeason?.year}`,
        appendDate: false,
      }}
      // Needed to differentiate from grower export button.
      buttonId='export-block-changes-btn'
    />
  );

  const sharedTableProps: DataTableProps<GrowerBlockTableItem> = {
    title: 'Blocks',
    unitToPaginate: 'blocks',
    isLoading,
    isFetching,
    columns: isMobile ? columnsMobile : columns,
    data: tableData,
    pagination,
    onRowClick: block => `${Constants.routes.GROWER_BLOCK_DETAILS}${block.id}`,
    filterBy: {
      filterParams,
      selectedFilters: filters,
      applyFilters,
    },
    loadError: !!getBlocksError,
    ...(showExportButton && { FooterComponent: ExportBlockChangesBtn }),
  };

  useEffect(() => {
    if (getBlocksError) {
      handleError(getBlocksError, 'Unable to load block changes');
    }
  }, [getBlocksError]);

  return (
    <>
      {isMobile ? (
        <MobileStyling>
          <DataTableMobile<GrowerBlockTableItem> {...sharedTableProps} />
        </MobileStyling>
      ) : (
        <DataTable<GrowerBlockTableItem> {...sharedTableProps} />
      )}
    </>
  );
};
